import React from 'react';

import { Link as RouterLink } from '~/lib/react-router-dom';

import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  List,
  ListItem,
  ListItemText,
  Typography,
} from '@mui/material';

import { CLAIM_CAMPAIGNS_URL } from '~/utils/societePayloadBuilder/statusCampaign/constants';
import type { CampaignMode } from '~/utils/societePayloadBuilder/statusCampaign/types';

import type { PayloadBuilderMode } from './PayloadBuilderToolbar';

interface PayloadBuilderSetupGuideModalProps {
  selectedMode: PayloadBuilderMode;
  open: boolean;
  onClose: () => void;
}

const LAB_FEATURE_FLAG: Record<CampaignMode, { name: string; description: string }> = {
  statusMatch: {
    name: 'enableStatusMatch',
    description: 'Powers the Status Match campaign page, application flow, and in-app challenge card.',
  },
  statusClaim: {
    name: 'enableTierUpgradeCampaign',
    description: 'Powers the partner Status Claim landing page and tier upgrade experience.',
  },
};

const OFFERS_HUB_REMOTE_FLAG = 'loyalty:societeOffersHubPage';
const WHATS_NEW_CAROUSEL_REMOTE_FLAG = 'loyalty:societeWhatsNewCarousel';

function Step({ number, title, children }: { number: number; title: string; children: React.ReactNode }) {
  return (
    <ListItem alignItems="flex-start" sx={{ px: 0, py: 1.5 }}>
      <Box
        sx={{
          minWidth: 28,
          height: 28,
          borderRadius: '50%',
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 13,
          fontWeight: 700,
          mr: 1.5,
          mt: 0.25,
          flexShrink: 0,
        }}
      >
        {number}
      </Box>
      <ListItemText
        primary={
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
            {title}
          </Typography>
        }
        secondary={
          <Typography variant="body2" component="div" color="text.secondary">
            {children}
          </Typography>
        }
      />
    </ListItem>
  );
}

function CampaignSetupGuideContent() {
  return (
    <>
      <DialogTitle>How to create a Status Match or Claim campaign</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          This page builds the JSON payload for the customer site. You will paste that JSON into a Lab feature flag
          after creating the backend claim campaign. Follow the steps below in order.
        </Typography>

        <List disablePadding>
          <Step number={1} title="Create the claim campaign in admin">
            <>
              Open{' '}
              <Link component={RouterLink} to={CLAIM_CAMPAIGNS_URL} target="_blank" rel="noopener noreferrer">
                Société → Claim Campaigns
              </Link>
              , click <strong>Create campaign</strong>, and configure the campaign (validation type, tiers, partner
              rules, and so on). Save the campaign, then copy its <strong>Campaign ID</strong> — you will need it in the
              payload builder and it must match the <code>campaignId</code> in your JSON.
            </>
          </Step>

          <Step number={2} title="Build the payload on this page">
            <>
              Select <strong>Status Match</strong> or <strong>Status Claim</strong> above, complete each section, and
              use the live preview to check the JSON. When all required fields are filled, use{' '}
              <strong>Copy to clipboard</strong> or <strong>Download .txt</strong>. Keep this JSON handy for the Lab
              step.
            </>
          </Step>

          <Step number={3} title="Create a Lab release toggle">
            <>
              Go to{' '}
              <Link component={RouterLink} to="/lab" target="_blank" rel="noopener noreferrer">
                Lab
              </Link>{' '}
              and create a new campaign. Recommended settings:
              <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2.5 }}>
                <li>
                  <strong>Name</strong> — something descriptive (e.g. &quot;Loyalty Status Match UK&quot;)
                </li>
                <li>
                  <strong>Type</strong> — Simple release toggle
                </li>
                <li>
                  <strong>Schedule</strong> — set start and end in <strong>Australia/Sydney</strong>; align with your
                  application period in the payload where relevant
                </li>
                <li>
                  <strong>Priority</strong> — any value is fine if only one campaign is active; higher numbers win when
                  multiple overlap
                </li>
              </Box>
            </>
          </Step>

          <Step number={4} title="Add the feature flag and paste your payload">
            <>
              In the Lab campaign, open <strong>Feature flags</strong> and add a flag:
              <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2.5 }}>
                <li>
                  <strong>Flag name</strong> — use the flag that matches your payload type:
                  <Box component="ul" sx={{ mt: 0.5, mb: 0, pl: 2.5 }}>
                    <li>
                      <code>{LAB_FEATURE_FLAG.statusMatch.name}</code> — {LAB_FEATURE_FLAG.statusMatch.description}
                    </li>
                    <li>
                      <code>{LAB_FEATURE_FLAG.statusClaim.name}</code> — {LAB_FEATURE_FLAG.statusClaim.description}
                    </li>
                  </Box>
                </li>
                <li>
                  <strong>Variant</strong> — control (unless you are running an A/B test)
                </li>
                <li>
                  <strong>Audience</strong> — Everyone, or a targeted audience if the campaign is region-specific
                </li>
                <li>
                  <strong>Payload</strong> — paste the full JSON from this payload builder (single JSON object, not an
                  array)
                </li>
              </Box>
              Save the feature flag configuration.
            </>
          </Step>

          <Step number={5} title="Roll out and activate">
            <>
              Under <strong>Campaign rollout</strong>, set the rollout percentage (typically 100% for a full launch, or
              lower for a gradual rollout). Save rollout settings, then set the campaign status to{' '}
              <strong>Active</strong>. The customer site will read the matching feature flag (
              <code>{LAB_FEATURE_FLAG.statusMatch.name}</code> or <code>{LAB_FEATURE_FLAG.statusClaim.name}</code>) and
              render the campaign using your payload.
            </>
          </Step>
        </List>
      </DialogContent>
    </>
  );
}

function OffersHubSetupGuideContent() {
  return (
    <>
      <DialogTitle>How to customise the offers hub page</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          This page builds the JSON payload for the Société Offers Hub page. Start from the existing Lab flag payload,
          make your changes here, then paste the validated JSON back into the same Lab remote flag.
        </Typography>

        <List disablePadding>
          <Step number={1} title="Copy the current Lab payload">
            <>
              Open{' '}
              <Link component={RouterLink} to="/lab" target="_blank" rel="noopener noreferrer">
                Lab
              </Link>
              , go to <strong>Feature flags → Remote Flag</strong>, find <code>{OFFERS_HUB_REMOTE_FLAG}</code>, and copy
              the existing payload.
            </>
          </Step>

          <Step number={2} title="Load it into the payload builder">
            <>
              Select <strong>Offers Hub</strong>, paste the JSON into <strong>Load existing payload</strong>, then load
              it so the form and live preview are populated from the current flag.
            </>
          </Step>

          <Step number={3} title="Customise the page content">
            <>
              Add, remove, rearrange, or deactivate hero carousel items and offers. Update images, copy, CTA details,
              enabled regions, and dates, then make sure the live preview shows no validation errors.
            </>
          </Step>

          <Step number={4} title="Paste the updated payload back into Lab">
            <>
              Use <strong>Copy to clipboard</strong> from the live preview, go back to{' '}
              <strong>Feature flags → Remote Flag</strong>, open <code>{OFFERS_HUB_REMOTE_FLAG}</code>, paste the JSON
              into the payload section, and save.
            </>
          </Step>
        </List>
      </DialogContent>
    </>
  );
}

function WhatsNewCarouselSetupGuideContent() {
  return (
    <>
      <DialogTitle>How to customise the What&apos;s New carousel</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          This page builds the JSON payload for the Société What&apos;s New carousel. Start from the existing Lab flag
          payload, make your changes here, then paste the validated JSON back into the same Lab remote flag.
        </Typography>

        <List disablePadding>
          <Step number={1} title="Copy the current Lab payload">
            <>
              Open{' '}
              <Link component={RouterLink} to="/lab" target="_blank" rel="noopener noreferrer">
                Lab
              </Link>
              , go to <strong>Feature flags → Remote Flag</strong>, find <code>{WHATS_NEW_CAROUSEL_REMOTE_FLAG}</code>,
              and copy the existing payload.
            </>
          </Step>

          <Step number={2} title="Load it into the payload builder">
            <>
              Select <strong>What&apos;s New Carousel</strong>, paste the JSON into{' '}
              <strong>Load existing payload</strong>, then load it so the form and live preview are populated from the
              current flag.
            </>
          </Step>

          <Step number={3} title="Customise the carousel cards">
            <>
              Add, remove, rearrange, or deactivate carousel cards. Update images, copy, CTA details, enabled regions,
              optional image fit, Optimizely feature flags, and dates, then make sure the live preview shows no
              validation errors.
            </>
          </Step>

          <Step number={4} title="Paste the updated payload back into Lab">
            <>
              Use <strong>Copy to clipboard</strong> from the live preview, go back to{' '}
              <strong>Feature flags → Remote Flag</strong>, open <code>{WHATS_NEW_CAROUSEL_REMOTE_FLAG}</code>, paste
              the JSON into the payload section, and save.
            </>
          </Step>
        </List>
      </DialogContent>
    </>
  );
}

function SetupGuideContent({ selectedMode }: { selectedMode: PayloadBuilderMode }) {
  if (selectedMode === 'offersHub') {
    return <OffersHubSetupGuideContent />;
  }

  if (selectedMode === 'whatsNewCarousel') {
    return <WhatsNewCarouselSetupGuideContent />;
  }

  return <CampaignSetupGuideContent />;
}

export default function PayloadBuilderSetupGuideModal({
  selectedMode,
  open,
  onClose,
}: PayloadBuilderSetupGuideModalProps) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <SetupGuideContent selectedMode={selectedMode} />
      <DialogActions>
        <Button variant="contained" onClick={onClose}>
          Got it
        </Button>
      </DialogActions>
    </Dialog>
  );
}
